import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { MultiChoiceOption } from '@/types/mcqOptionType';
import type { ReviewQuestion, ReviewQuestionOption } from '@/types/review/ReviewQuestion';
import type { DraggableItem } from '@amelio/ui-library';

import McqOptionList from '@/components/McqOptionList/McqOptionList';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { filterOutOtherOption } from '@/utils/question';

interface MultiChoiceQuestionProps {
    question: ReviewQuestion;
    setQuestion: (question: ReviewQuestion) => void;
    // Competency-page-only: hide the "Add the 'Other' option" toggle. Undefined elsewhere.
    hideOtherOption?: boolean;
    // Number of blank option fields seeded for an empty multiple-choice. Neighbors (360/cycle/
    // template) keep develop's single blank; the competency page opts into 3 via questionDefaults.
    emptyOptionCount?: number;
}

export const MultiChoiceQuestion = ({
    question,
    setQuestion,
    hideOtherOption,
    emptyOptionCount = 1,
}: MultiChoiceQuestionProps) => {
    const [hasOtherOption, setHasOtherOption] = useState<boolean>(question.hasOtherOption ?? false);
    const defaultLanguage = useSelectedAppLanguage();
    const isMounted = useRef(false);
    const { t } = useTranslation();

    const options = filterOutOtherOption(question.reviewQuestionOptions);

    const DEFAULT_MULTI_CHOICE_OPTIONS = [
        t('evaluation360Questionnaire.questionForm.questions.likertScale.stronglyAgree'),
        t('evaluation360Questionnaire.questionForm.questions.likertScale.somewhatAgree'),
        t('evaluation360Questionnaire.questionForm.questions.likertScale.neutral'),
        t('evaluation360Questionnaire.questionForm.questions.likertScale.somewhatDisagree'),
        t('evaluation360Questionnaire.questionForm.questions.likertScale.stronglyDisagree'),
    ];

    useEffect(() => {
        if (options.length !== 0) return;

        const isFirstMount = !isMounted.current;
        isMounted.current = true;

        const newOptions =
            isFirstMount && !question.id
                ? DEFAULT_MULTI_CHOICE_OPTIONS.map((label, index) => ({
                      id: crypto.randomUUID(),
                      label: { [defaultLanguage]: label },
                      sortOrder: index,
                      scoreValue: 0,
                  }))
                : // Seed the configured number of blank option fields (default 1 = develop
                  // behavior for 360/cycle/template; the competency page opts into 3).
                  Array.from({ length: emptyOptionCount }, (_, index) => ({
                      id: crypto.randomUUID(),
                      label: { [defaultLanguage]: '' },
                      sortOrder: index,
                      scoreValue: 0,
                  }));

        setQuestion({
            ...question,
            reviewQuestionOptions: newOptions,
        });
    }, [options.length, question.id, defaultLanguage, question, setQuestion]);

    const draggableOptions = useMemo(
        () =>
            options.map((option) => ({
                id: option.id,
                data: { label: option.label },
            })),
        [options],
    );

    const setOptions = (draggableItems: DraggableItem<MultiChoiceOption>[]) => {
        const questionOptionItems = draggableItems.map((item, index) => {
            const existingOption = options.find((opt) => opt.id === item.id);
            return {
                id: item.id,
                label: item.data.label,
                sortOrder: index,
                scoreValue: existingOption?.scoreValue ?? 0,
                reviewQuestionId: existingOption?.reviewQuestionId,
            };
        });
        setQuestion({
            ...question,
            reviewQuestionOptions: questionOptionItems,
        });
    };

    const updateOption = (id: string, value: string) => {
        const existingOption = options.find((opt) => opt.id === id);

        if (!existingOption) {
            const newOption: ReviewQuestionOption = {
                id,
                label: { [defaultLanguage]: value },
                sortOrder: options.length,
                scoreValue: 0,
            };
            setQuestion({
                ...question,
                reviewQuestionOptions: [...options, newOption],
            });
            return;
        }

        const updated = options.map((option) => {
            if (option.id === id) {
                return {
                    id: option.id,
                    label: { ...option.label, [defaultLanguage]: value },
                    sortOrder: option.sortOrder,
                    scoreValue: option.scoreValue,
                    reviewQuestionId: option.reviewQuestionId,
                };
            }
            return option;
        });
        setQuestion({
            ...question,
            reviewQuestionOptions: updated,
        });
    };

    const deleteOption = (id: string) => {
        const updated = options.filter((option) => option.id !== id);
        setQuestion({
            ...question,
            reviewQuestionOptions: updated.map((option, index) => ({
                ...option,
                sortOrder: index,
            })),
        });
    };

    const addOption = () => {
        // The "+" always adds a new empty option, even when the previous one is still
        // blank — the user can lay out several choice fields before filling them in.
        // Empty options remain rejected by the save-time validation.
        const newOption: ReviewQuestionOption = {
            id: crypto.randomUUID(),
            label: { [defaultLanguage]: '' },
            sortOrder: options.length,
            scoreValue: 0,
        };

        setQuestion({
            ...question,
            reviewQuestionOptions: [...options, newOption],
        });
    };

    const handleOtherChange = () => {
        setHasOtherOption(!hasOtherOption);
        setQuestion({
            ...question,
            hasOtherOption: !hasOtherOption,
        });
    };

    return (
        <McqOptionList
            options={draggableOptions}
            setOptions={setOptions}
            updateOption={updateOption}
            deleteOption={deleteOption}
            addOptionHandler={addOption}
            handleOtherChange={handleOtherChange}
            isOtherOption={hasOtherOption}
            hideOtherOption={hideOtherOption || !!question.companyValueId}
        />
    );
};
